import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Container } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext, useToast, useTranslator } from '@front-activerse/common-components';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { useCameraCapture } from '../hooks';
import {
  AddUserProductFormValues,
  SCAN_LABEL_RESULT_EVENT,
  SCAN_LABEL_RESULT_STORAGE_KEY,
} from '../AddUserProduct/helpers';

const INTRO_SEEN_KEY = 'tracker_scan_label_intro_seen';

type ScanStep = 'front' | 'nutrition' | 'analyzing';

interface TrackerScanLabelContainerProps {
  scanLabel: (images: File[], userComment?: string) => Promise<unknown>;
  cancelScanLabel: () => void;
}

const TrackerScanLabelContainer: React.FC<TrackerScanLabelContainerProps> = ({
  scanLabel,
  cancelScanLabel,
}) => {
  const { t } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();
  const formValues = (location.state as { formValues?: AddUserProductFormValues } | null)
    ?.formValues;
  const { toastError } = useToast();

  const {
    images: {
      cameraShutter,
      closeWhiteIcon,
      flashOnIcon,
      flashOffIcon,
      photoGalleryIcon,
      videoPoster,
      introFrontIcon,
      introNutritionIcon,
      scanInfoIcon,
    },
  } = useAppContext();

  const camera = useCameraCapture();
  const [step, setStep] = useState<ScanStep>('front');
  const [showIntro, setShowIntro] = useState(() => !localStorage.getItem(INTRO_SEEN_KEY));
  const [frontThumbUrl, setFrontThumbUrl] = useState<string | null>(null);
  const [nutritionThumbUrl, setNutritionThumbUrl] = useState<string | null>(null);
  const [scanError, setScanError] = useState<
    'noFood' | 'rateLimit' | 'aiAccessDenied' | 'generic' | null
  >(null);
  const frontPhotoRef = useRef<File | null>(null);
  const abandonedRef = useRef(false);
  const scanAreaRef = useRef<HTMLDivElement | null>(null);

  // Camera lifecycle — tied to this being the visible route, NOT to mount/unmount:
  // SlideRoutes renders with destroy={false}, so this component stays mounted after
  // navigating away (back gesture, TitleBar back, successful scan). A mount-effect
  // cleanup would never run and the device camera would stay on. `useLocation` returns
  // the live router location even for kept-mounted pages, so it tells us when we leave.
  const isActiveRoute = location.pathname.endsWith('/scan-label');
  useEffect(() => {
    if (!isActiveRoute) return undefined;
    let active = true;
    const init = async () => {
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (active) await camera.startCamera();
    };
    init();
    const stop = () => camera.stopCamera();
    window.addEventListener('beforeunload', stop);
    window.addEventListener('pagehide', stop);
    return () => {
      active = false;
      camera.stopCamera();
      window.removeEventListener('beforeunload', stop);
      window.removeEventListener('pagehide', stop);
      // Reset the scan state so a later re-entry (component may still be mounted)
      // starts from a fresh step 1/2 instead of a stale 2/2 with old thumbs.
      frontPhotoRef.current = null;
      setStep('front');
      setScanError(null);
      setFrontThumbUrl(null);
      setNutritionThumbUrl(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveRoute]);

  useEffect(() => {
    return () => {
      if (frontThumbUrl) URL.revokeObjectURL(frontThumbUrl);
    };
  }, [frontThumbUrl]);

  useEffect(() => {
    return () => {
      if (nutritionThumbUrl) URL.revokeObjectURL(nutritionThumbUrl);
    };
  }, [nutritionThumbUrl]);

  const dismissIntro = () => {
    localStorage.setItem(INTRO_SEEN_KEY, 'true');
    setShowIntro(false);
  };

  const openIntro = () => setShowIntro(true);

  const analyze = async (front: File, nutrition: File) => {
    setStep('analyzing');
    await camera.stopCamera();
    // The TitleBar back button (and browser back) leaves this screen without aborting the
    // request; when the response lands after the user already navigated away, acting on it
    // would navigate(-1) a second time and pop the form off the history stack. SlideRoutes
    // keeps this component mounted (destroy={false}), so track leaving via popstate.
    abandonedRef.current = false;
    const markAbandoned = () => {
      abandonedRef.current = true;
    };
    window.addEventListener('popstate', markAbandoned);
    try {
      const response: any = await scanLabel([front, nutrition]);
      if (abandonedRef.current) return;
      if (response?.__typename === 'AnalyzedDailyTrackerIngredients') {
        const payload = {
          primaryIngredient: response.primaryIngredient,
          ...(response.requestId && { requestId: response.requestId }),
          ...(formValues && { formValues }),
          updatedAt: Date.now(),
        };
        sessionStorage.setItem(SCAN_LABEL_RESULT_STORAGE_KEY, JSON.stringify(payload));
        window.dispatchEvent(new CustomEvent(SCAN_LABEL_RESULT_EVENT, { detail: payload }));
        navigate(-1);
      } else if (response?.__typename === 'NotFoodError') {
        setScanError('noFood');
      } else if (response?.__typename === 'RateLimitExceeded') {
        setScanError('rateLimit');
      } else if (response?.__typename === 'AiAccessDenied') {
        setScanError('aiAccessDenied');
      } else {
        setScanError('generic');
      }
    } catch {
      // A cancel (Cancel button / back navigation aborts the request) must not surface as an
      // error on this — by then hidden — screen.
      if (!abandonedRef.current) setScanError('generic');
    } finally {
      window.removeEventListener('popstate', markAbandoned);
    }
  };

  const handleCapture = async (file: File) => {
    if (step === 'front') {
      frontPhotoRef.current = file;
      setFrontThumbUrl(URL.createObjectURL(file));
      setStep('nutrition');
    } else if (step === 'nutrition' && frontPhotoRef.current) {
      setNutritionThumbUrl(URL.createObjectURL(file));
      await analyze(frontPhotoRef.current, file);
    }
  };

  const handleShutter = async () => {
    if (camera.isCapturing || step === 'analyzing') return;
    try {
      // Crop exactly to the white scan frame the user aims with, at full source resolution
      // (no downscaling) and high JPEG quality — the nutrition table fills the sent image,
      // which is what the model actually needs to read small print.
      const file = await camera.capturePhoto({
        crop: 'element',
        cropElement: scanAreaRef.current,
        quality: 0.95,
        fileName: step === 'front' ? 'label-front.jpg' : 'label-nutrition.jpg',
      });
      await handleCapture(file);
    } catch {
      toastError();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Gallery uploads have no scan frame to crop to — send the whole photo, but keep it under
    // the backend's 1MB-per-image limit (1600px + q0.92 instead of the legacy 1024px + q0.9).
    const file = await camera.processUploadedFile(event, {
      crop: 'scale',
      maxDimension: 1600,
      quality: 0.92,
      fileName: step === 'front' ? 'label-front.jpg' : 'label-nutrition.jpg',
    });
    if (file) await handleCapture(file);
  };

  const handleRetry = async () => {
    setScanError(null);
    frontPhotoRef.current = null;
    if (frontThumbUrl) URL.revokeObjectURL(frontThumbUrl);
    setFrontThumbUrl(null);
    if (nutritionThumbUrl) URL.revokeObjectURL(nutritionThumbUrl);
    setNutritionThumbUrl(null);
    setStep('front');
    await camera.startCamera();
  };

  const handleClose = async () => {
    abandonedRef.current = true;
    cancelScanLabel();
    await camera.stopCamera();
    navigate(-1);
  };

  // --- render ---
  // analyzing state — Figma "scanner.label.analyzing.withPhotos" (8839:43741): white page with
  // navbar, the voice-log animated circle, duration hint, both captured photos and Cancel.
  if (step === 'analyzing' && !scanError) {
    return (
      <Layout>
        <TitleBar showBackBtn={true}>{t('tracker.scanLabel.navbarTitle')}</TitleBar>

        <div className="page-template-tracker edit-meal-page">
          <Container className="py-md-4 full-height d-flex flex-column">
            <div className="tracker-container text-center d-flex flex-column flex-grow-1">
              <div className="voice-button is-submitting" data-testid="scan-label-analyzing">
                <div className="loader-container">
                  <p className="mb-0 medium">{t('tracker.describeFood.loader.analyzing')}</p>
                </div>
              </div>

              <p className="description text-text-variant-color">
                {t('tracker.scanner.analyzing.duration')}
              </p>

              {frontThumbUrl && (
                <div className="d-flex justify-content-center gap-m my-l">
                  <div className="captured-photo-item">
                    <div className="captured-photo captured-photo-lg">
                      <img src={frontThumbUrl} alt="" />
                      <span className="captured-photo-badge">
                        {t('tracker.scanner.captured.done')}
                      </span>
                    </div>
                    <p className="description mb-0">
                      {t('tracker.scanner.analyzing.imageLabel.front')}
                    </p>
                  </div>
                  {nutritionThumbUrl && (
                    <div className="captured-photo-item">
                      <div className="captured-photo captured-photo-lg">
                        <img src={nutritionThumbUrl} alt="" />
                        <span className="captured-photo-badge">
                          {t('tracker.scanner.captured.done')}
                        </span>
                      </div>
                      <p className="description mb-0">
                        {t('tracker.scanner.analyzing.imageLabel.nutrition')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <Button
                color="secondary"
                outline
                className="w-100 mt-auto mb-l"
                onClick={handleClose}
                data-testid="scan-label-cancel"
              >
                {t('common.cancel')}
              </Button>
            </div>
          </Container>
        </div>
      </Layout>
    );
  }

  return (
    <div className="scan-plate">
      <video
        ref={camera.videoRef}
        className="camera-video-fullscreen"
        poster={videoPoster}
        playsInline
        muted
        style={{ opacity: camera.isVideoVisible ? 1 : 0 }}
      />
      <canvas ref={camera.canvasRef} className="d-none" />
      <input
        ref={camera.fileInputRef}
        type="file"
        accept="image/*"
        className="d-none"
        onChange={handleFileUpload}
      />

      <div className="camera-overlay-fullscreen">
        {camera.isCameraActive && <div className="scan-area" ref={scanAreaRef} />}

        {frontThumbUrl && step === 'nutrition' && !scanError && !camera.error && (
          <div
            className="captured-photo captured-photo-anchored"
            data-testid="scan-label-front-thumb"
          >
            <img src={frontThumbUrl} alt="" />
            <span className="captured-photo-badge">{t('tracker.scanner.captured.done')}</span>
          </div>
        )}

        <div className="top-controls">
          <button
            className="scan-plate-button"
            onClick={handleClose}
            data-testid="scan-label-close"
          >
            <img src={closeWhiteIcon} alt="" />
          </button>
          <p className="mb-0 scan-steps-pill">
            <span className="semi-bold">
              {t('tracker.scanner.steps.current', {
                current: step === 'front' ? 1 : 2,
                total: 2,
              })}
            </span>{' '}
            {t('tracker.scanner.steps.separator')}{' '}
            <span>
              {step === 'front'
                ? t('tracker.scanner.steps.frontOfProduct')
                : t('tracker.scanner.steps.nutritionLabel')}
            </span>
          </p>
          <button
            className="scan-plate-button"
            onClick={openIntro}
            data-testid="scan-label-info"
            aria-label={t('tracker.scanner.intro.title')}
          >
            <img src={scanInfoIcon} alt="" />
          </button>
        </div>

        <div className="bottom-controls">
          {scanError ? (
            <div className="text-center w-100">
              <Alert
                color={scanError === 'rateLimit' ? 'danger' : 'warning'}
                className="mb-m"
                fade={false}
              >
                <p className="mb-0 text-text-main-color text-start">
                  {scanError === 'noFood' && t('tracker.describeFood.analyze.noFood.notification')}
                  {scanError === 'rateLimit' && t('tracker.limitUsageError.notification')}
                  {scanError === 'aiAccessDenied' &&
                    t('tracker.scanner.aiAccessDenied.notification')}
                  {scanError === 'generic' && t('common.error.unexpected')}
                </p>
              </Alert>
              <Button color="primary" onClick={handleRetry} data-testid="scan-label-retry">
                {t('tracker.scan.button.refreshCamera')}
              </Button>
            </div>
          ) : camera.error && step !== 'analyzing' ? (
            <div className="text-center w-100">
              <Alert color="danger" className="mb-0" fade={false}>
                <p className="mb-0 text-text-main-color text-start">
                  {t('tracker.scanFood.noAccessCamera.notification')}
                </p>
              </Alert>
              {!camera.isCameraActive && !camera.isSearchingCamera && (
                <Button
                  color="link"
                  size="sm"
                  onClick={() => window.location.reload()}
                  disabled={camera.isSearchingCamera || camera.isCapturing}
                >
                  <p>{t('tracker.scan.button.refreshCamera')}</p>
                </Button>
              )}
              <button className="scan-plate-button" onClick={camera.openGallery}>
                <img src={photoGalleryIcon} alt="" />
              </button>
            </div>
          ) : (
            <>
              {camera.isTorchSupported ? (
                <button className="scan-plate-button" onClick={camera.toggleTorch}>
                  <img src={camera.isTorchOn ? flashOnIcon : flashOffIcon} alt="" />
                </button>
              ) : (
                <div style={{ width: 40, height: 40 }} />
              )}
              <button
                className="scan-plate-button"
                onClick={handleShutter}
                disabled={camera.isCapturing}
                data-testid="scan-label-shutter"
                data-test-id="scan_label_shutter"
              >
                <img src={cameraShutter} alt="" />
              </button>
              <button className="scan-plate-button" onClick={camera.openGallery}>
                <img src={photoGalleryIcon} alt="" />
              </button>
            </>
          )}
        </div>
      </div>

      {showIntro && (
        <div
          className="scan-label-intro-backdrop"
          onClick={dismissIntro}
          data-testid="scan-label-intro"
        >
          {/* Bottom sheet (Figma "Tips sheet") — stop backdrop-close when tapping inside */}
          <div className="scan-label-intro-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="scan-label-intro-images">
              <div className="scan-label-intro-image">
                <div className="scan-label-intro-thumb">
                  <img src={introFrontIcon} alt="" />
                </div>
                <p className="description mb-0">{t('tracker.scanner.intro.imageLabel.front')}</p>
              </div>
              <div className="scan-label-intro-image">
                <div className="scan-label-intro-thumb">
                  <img src={introNutritionIcon} alt="" />
                </div>
                <p className="description mb-0">
                  {t('tracker.scanner.intro.imageLabel.nutrition')}
                </p>
              </div>
            </div>

            <h2 className="title-small bold text-center mt-l mb-xs">
              {t('tracker.scanner.intro.title')}
            </h2>
            <p className="text-text-variant-color text-center mb-l">
              {t('tracker.scanner.intro.description')}
            </p>

            <Button
              color="primary"
              className="w-100"
              onClick={dismissIntro}
              data-testid="scan-label-intro-cta"
            >
              {t('tracker.scanner.intro.cta')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackerScanLabelContainer;
